// src/admin/WhyChooseUsManagement.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Save, Star, Plus, Trash2, AlertCircle, CheckCircle, Eye,
  Truck, Shield, Heart, Zap, Award, Users, TrendingUp, Lock,
  Leaf, Gift, Bell, RefreshCw, CheckCircle as CheckIcon, Clock,
  ThumbsUp, Package, Home, Smartphone, DollarSign
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const WhyChooseUsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);

  const defaultConfig = {
    active: true,
    title: 'Why Choose Organic Land?',
    subtitle: 'Hum kyun best hain aapke liye',
    features: [
      { icon: 'truck', title: 'Free Shipping', description: 'Rs. 2000 se upar ke orders par free delivery' },
      { icon: 'shield', title: '100% Organic', description: 'Certified organic products sourced from farms' },
      { icon: 'refresh', title: '7-Day Returns', description: 'Agar pasand na aaye, easy return policy' },
      { icon: 'thumbsup', title: 'Quality Guaranteed', description: 'Fresh products har baar, guaranteed' },
    ],
  };

  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'why-choose-us');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig({ ...defaultConfig, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await setDoc(doc(db, 'settings', 'why-choose-us'), config, { merge: true });
      setMessage({ type: 'success', text: 'Why Choose Us section updated!' });
      setShowSuccess(true);
      window.dispatchEvent(new Event('whyChooseUsUpdated'));
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if ((config.features || []).length >= 8) {
      alert('Maximum 8 features tak add kar sakte hain.');
      return;
    }
    setConfig({
      ...config,
      features: [...(config.features || []), { icon: 'check', title: '', description: '' }],
    });
  };

  const updateFeature = (index, field, value) => {
    const updated = [...config.features];
    updated[index] = { ...updated[index], [field]: value };
    setConfig({ ...config, features: updated });
  };

  const removeFeature = (index) => {
    setConfig({
      ...config,
      features: config.features.filter((_, i) => i !== index),
    });
  };

  const iconOptions = [
    { value: 'truck', label: '🚚 Truck', Icon: Truck },
    { value: 'shield', label: '🛡️ Shield', Icon: Shield },
    { value: 'heart', label: '❤️ Heart', Icon: Heart },
    { value: 'zap', label: '⚡ Zap', Icon: Zap },
    { value: 'award', label: '🏆 Award', Icon: Award },
    { value: 'users', label: '👥 Users', Icon: Users },
    { value: 'trending', label: '📈 Trending', Icon: TrendingUp },
    { value: 'lock', label: '🔒 Lock', Icon: Lock },
    { value: 'leaf', label: '🌿 Leaf', Icon: Leaf },
    { value: 'gift', label: '🎁 Gift', Icon: Gift },
    { value: 'bell', label: '🔔 Bell', Icon: Bell },
    { value: 'refresh', label: '🔄 Refresh', Icon: RefreshCw },
    { value: 'check', label: '✅ Check', Icon: CheckIcon },
    { value: 'clock', label: '⏰ Clock', Icon: Clock },
    { value: 'thumbsup', label: '👍 Thumbs Up', Icon: ThumbsUp },
    { value: 'package', label: '📦 Package', Icon: Package },
    { value: 'star', label: '⭐ Star', Icon: Star },
    { value: 'home', label: '🏠 Home', Icon: Home },
    { value: 'phone', label: '📱 Phone', Icon: Smartphone },
    { value: 'dollar', label: '💵 Dollar', Icon: DollarSign },
  ];

  const getIconComponent = (iconName) => {
    const found = iconOptions.find(o => o.value === iconName);
    return found ? found.Icon : CheckIcon;
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Star className="text-green-700" size={26} /> Why Choose Us Section
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Home page par "Why Choose Us" section edit karein.
          </p>
        </div>
        <a href="/" target="_blank" rel="noopener noreferrer"
          className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-5 py-3 rounded-lg transition flex items-center gap-2 font-semibold border border-blue-200">
          <Eye size={18} /> Preview
        </a>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">

        {/* Visibility */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Section Visibility</h2>
          <label className="flex items-center gap-3 cursor-pointer p-4 rounded-lg bg-green-50 border border-green-200">
            <input type="checkbox" checked={config.active}
              onChange={(e) => setConfig({ ...config, active: e.target.checked })}
              className="w-5 h-5 text-green-700" />
            <div>
              <span className="text-sm font-semibold text-gray-800 block">
                Show "Why Choose Us" section on home page
              </span>
              <span className="text-xs text-gray-600">
                Uncheck karne par section hide ho jayega
              </span>
            </div>
          </label>
        </div>

        {/* Titles */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-5">Section Titles</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Main Title</label>
              <input type="text" value={config.title}
                onChange={(e) => setConfig({ ...config, title: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                placeholder="Why Choose Organic Land?" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Subtitle</label>
              <input type="text" value={config.subtitle}
                onChange={(e) => setConfig({ ...config, subtitle: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                placeholder="Hum kyun best hain aapke liye" />
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800">
              Features ({(config.features || []).length}/8)
            </h2>
            <button type="button" onClick={addFeature}
              className="text-sm font-semibold text-green-700 hover:text-green-800 flex items-center gap-1">
              <Plus size={14} /> Add Feature
            </button>
          </div>

          <div className="space-y-4">
            {(config.features || []).map((feature, index) => {
              const IconComp = getIconComponent(feature.icon);
              return (
                <div key={index} className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                  <div className="flex items-start gap-3">
                    {/* Icon Preview */}
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-green-100 shrink-0">
                      <IconComp size={22} className="text-green-700" />
                    </div>

                    <div className="flex-1 space-y-3">
                      {/* Icon Select */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Icon</label>
                        <select value={feature.icon}
                          onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:border-green-600 bg-white">
                          {iconOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Title */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Title</label>
                        <input type="text" value={feature.title}
                          onChange={(e) => updateFeature(index, 'title', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:border-green-600"
                          placeholder="e.g., Free Shipping" />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                        <input type="text" value={feature.description}
                          onChange={(e) => updateFeature(index, 'description', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:border-green-600"
                          placeholder="Short description..." />
                      </div>
                    </div>

                    {/* Delete */}
                    <button type="button" onClick={() => removeFeature(index)}
                      disabled={(config.features || []).length === 1}
                      className="bg-red-50 hover:bg-red-100 text-red-600 rounded-lg p-2 disabled:opacity-40 shrink-0">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-500 mt-4">
            💡 Minimum 1, Maximum 8 features. Recommended: 4 features (best look on home page)
          </p>
        </div>

        {/* Save */}
        <div className="flex justify-end sticky bottom-4">
          <button type="submit" disabled={saving}
            className={`bg-green-700 text-white py-3 px-8 rounded-lg font-semibold hover:bg-green-800 transition flex items-center gap-2 shadow-xl ${
              saving ? 'opacity-70 cursor-not-allowed' : ''
            }`}>
            <Save size={18} /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

      </form>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message="Why Choose Us section updated!"
        buttonText="Great!"
      />

    </div>
  );
};

export default WhyChooseUsManagement;